import type { AdminMapSummary } from '../../../spacetime/SpaceTimeClient';
import type { AdminGridShape, AdminToolSelection, PlayerLookupHandler } from '../types';
import { createSectionLabel } from './common';
import {
  renderCreateMapSection,
  renderDeleteMapSection,
  renderMapToolActionsSection,
} from './mapUtilitiesSections';
import { renderPlayerUtilitiesSection } from './playerUtilitiesSection';

interface AdminConsoleState {
  isLoadingMapCatalog: boolean;
  mapCatalogStatus: string;
  isSavingFrontendMapAsset: boolean;
  isUploadingMapJson: boolean;
  isReloadingMaps: boolean;
  mapCatalog: AdminMapSummary[];
  createMapId: string;
  createMapName: string;
  createMapWidth: string;
  createMapHeight: string;
  createMapGridType: AdminGridShape;
  deleteMapId: string;
  selectedTool: AdminToolSelection | null;
  currentMapId: string;
  playerQuery: string;
  searchStatus: string | null;
  isSearching: boolean;
  isDisconnecting: boolean;
  isBringing: boolean;
}

interface AdminConsoleActions {
  render: () => void;
  setMapCatalogStatus: (value: string) => void;
  setSavingFrontendMapAsset: (value: boolean) => void;
  setUploadingMapJson: (value: boolean) => void;
  setReloadingMaps: (value: boolean) => void;
  setCreateMapId: (value: string) => void;
  setCreateMapName: (value: string) => void;
  setCreateMapWidth: (value: string) => void;
  setCreateMapHeight: (value: string) => void;
  setCreateMapGridType: (value: AdminGridShape) => void;
  setDeleteMapId: (value: string) => void;
  setSelectedTool: (tool: AdminToolSelection | null) => void;
  setPlayerQuery: (value: string) => void;
  setSearchStatus: (value: string | null) => void;
  setSearching: (value: boolean) => void;
  setDisconnecting: (value: boolean) => void;
  setBringing: (value: boolean) => void;
  onSaveFrontendMapAsset: () => Promise<string>;
  onUploadMapJson: () => Promise<boolean>;
  onWipeAndReloadMaps: () => Promise<void>;
  createMap: (input: { mapId: string; name: string; width: number; height: number; gridType: AdminGridShape }) => Promise<void>;
  deleteMap: (mapId: string) => Promise<void>;
  refreshMapCatalog: () => Promise<void>;
  scanPlayersOnMap: PlayerLookupHandler;
  disconnectPlayerSession: PlayerLookupHandler;
  bringPlayerHere: PlayerLookupHandler;
}

export function renderAdminConsoleTab(
  content: HTMLElement,
  state: AdminConsoleState,
  actions: AdminConsoleActions
): void {
  const scrollArea = document.createElement('div');
  Object.assign(scrollArea.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    flex: '1',
    minHeight: '0',
    overflowY: 'auto',
    paddingRight: '4px',
  });

  const info = createSectionLabel('Admin Console');

  const helpLine = document.createElement('div');
  helpLine.textContent = 'Manage maps and run player admin actions from one panel.';
  Object.assign(helpLine.style, { color: '#94a3b8', fontSize: '12px' });

  const statusLine = document.createElement('div');
  statusLine.textContent = state.isLoadingMapCatalog ? 'Loading maps...' : state.mapCatalogStatus || 'Admin tools ready.';
  Object.assign(statusLine.style, {
    color: '#f8fafc',
    fontSize: '12px',
    padding: '8px',
    borderRadius: '6px',
    background: 'rgba(15, 23, 42, 0.75)',
  });

  info.appendChild(helpLine);
  info.appendChild(statusLine);
  scrollArea.appendChild(info);

  scrollArea.appendChild(renderMapToolActionsSection(state, actions));
  scrollArea.appendChild(renderCreateMapSection(state, actions));
  scrollArea.appendChild(renderDeleteMapSection(state, actions));
  scrollArea.appendChild(renderPlayerUtilitiesSection(state, actions));
  content.appendChild(scrollArea);
}
